"use client";

import { ArrowLeft, ArrowRight, ImagePlus, X } from "lucide-react";
import {
  checkPhotoFile,
  PHOTO_MAX_COUNT,
  PHOTOS_TOO_MANY,
} from "@/modules/catalog/surfaces";
import { FIELD_LABEL } from "./ui";

/**
 * The photo grid of BusinessProfileEditScreen (elements 3b to 3e, H59).
 *
 * A photo is either one the clinic already shows (an address) or one picked
 * here and not uploaded yet (a file, with a local preview). The order of the
 * list is the order Consumers see, and the first is the cover. Nothing leaves
 * the browser until Save: the host uploads the files and sends the addresses.
 */
export type PhotoItem =
  | { kind: "existing"; url: string }
  | { kind: "new"; file: File; preview: string };

export function photoSrc(item: PhotoItem): string {
  return item.kind === "existing" ? item.url : item.preview;
}

/** Add picked files to the end, refusing the ones that fail the check. */
export function addPhotoFiles(
  photos: readonly PhotoItem[],
  files: readonly File[],
): { photos: PhotoItem[]; error: string | null } {
  const next = [...photos];
  let error: string | null = null;
  for (const file of files) {
    if (next.length >= PHOTO_MAX_COUNT) {
      error = PHOTOS_TOO_MANY;
      break;
    }
    const check = checkPhotoFile(file);
    if (!check.ok) {
      error = check.error;
      continue;
    }
    next.push({ kind: "new", file, preview: URL.createObjectURL(file) });
  }
  return { photos: next, error };
}

export function movePhoto(photos: readonly PhotoItem[], from: number, to: number): PhotoItem[] {
  if (to < 0 || to >= photos.length) return [...photos];
  const next = [...photos];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

export function PhotosEditor({
  photos,
  onChange,
  error,
  onError,
}: {
  photos: readonly PhotoItem[];
  onChange: (photos: PhotoItem[]) => void;
  error: string | null;
  onError: (error: string | null) => void;
}) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className={FIELD_LABEL}>Photos (optional)</span>
        <span className="text-xs text-muted">
          {photos.length} of {PHOTO_MAX_COUNT}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo, index) => (
          <div
            key={photoSrc(photo)}
            className="relative aspect-square overflow-hidden rounded-lg border border-line bg-surface"
          >
            <img src={photoSrc(photo)} alt="" className="h-full w-full object-cover" />
            {index === 0 && (
              <span className="absolute left-1.5 top-1.5 rounded-md bg-black/70 px-1.5 py-0.5 text-xs font-semibold text-white">
                Cover
              </span>
            )}
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/55 p-1">
              <button
                type="button"
                aria-label="Move earlier"
                disabled={index === 0}
                onClick={() => onChange(movePhoto(photos, index, index - 1))}
                className="rounded p-1 text-white disabled:opacity-30"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                type="button"
                aria-label="Remove"
                onClick={() => {
                  onChange(photos.filter((_, i) => i !== index));
                  onError(null);
                }}
                className="rounded p-1 text-white"
              >
                <X size={16} />
              </button>
              <button
                type="button"
                aria-label="Move later"
                disabled={index === photos.length - 1}
                onClick={() => onChange(movePhoto(photos, index, index + 1))}
                className="rounded p-1 text-white disabled:opacity-30"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        ))}
        {photos.length < PHOTO_MAX_COUNT && (
          <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-line bg-surface text-sm font-medium text-accent">
            <ImagePlus size={22} aria-hidden />
            Add photos
            {/* No name: the files are carried by the host, not by the form. */}
            <input
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={(e) => {
                const result = addPhotoFiles(photos, Array.from(e.target.files ?? []));
                onChange(result.photos);
                onError(result.error);
                e.target.value = "";
              }}
            />
          </label>
        )}
      </div>
      <p className="mt-1 text-xs text-muted">
        PNG, JPEG or WebP, up to 5 MB each. The first photo is the cover.
      </p>
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  );
}
